import Extend from "../utils/extend";
import { uuid } from "../utils/helpers";

/**
 * Tiered billing type editor.
 * This is Vue component and therefore requires Vue library.
 *
 * How to use it:
 *
 *     new solodev.TieredBillingTypeEditor({
 *       propsData: {
 *         input: <html input element>,
 *         validationApiEndpoint: '/path/to/validation/endpoint',
 *       },
 *       el: <html element where render component>,
 *     });
 *
 * @author Tomasz Zeludziewicz @ 2020-05-11
 */
const Vue = window.Vue || null;
const TieredBillingTypeEditor = Vue && Vue.extend({
  // HTML input where serialized JSON will be stored.
  props: {
    input: {
      required: true,
    },
    validationApiEndpoint: {
      type: String,
      required: true,
    }
  },
  data() {
    const data = {
      loading: false,
      error: null,
    };

    let inputValue = this.input.value;
    if (inputValue) {
      inputValue = JSON.parse(inputValue);
      if (Object.keys(inputValue).length !== 0) {
        data.items = inputValue;
      }
    }

    if (!data.items) {
      data.items = [
        this.createItem({
          start: {
            value: 1,
          },
        }),
      ];
    }

    return data;
  },
  template: `
  <div class="sd-tbte">
    <div class="sd-tbte__loading" v-show="loading"><img src="/___/images/loading.gif"></div>
    <div v-if="error" class="sd-tbte__general-error">{{ error }}</div>
    <div class="row sd-tbte__row" v-for="(item, index) in items" v-key="item.uuid">
      <div class="col-sm-3">
        <label>Starting Quantity</label>
        <div class="sd-tbte__input">
          <input
            type="text"
            class="form-control"
            disabled="true"
            v-model="item.start.value">
          <span>units</span>
        </div>
        <p class="sd-tbte__error" v-show="item.start.error">{{ item.start.error }}</p>
      </div>
      <div class="col-sm-3">
        <label>Ending Quantity</label>
        <div class="sd-tbte__input">
          <input
            type="text"
            class="form-control"
            :disabled="items.length-1 !== index"
            :value="item.end.value"
            @change="onEndingQuantityChange(index, $event)">
          <span>units</span>
        </div>
        <p class="sd-tbte__error" v-show="item.end.error">{{ item.end.error }}</p>
      </div>
      <div class="col-sm-3">
        <label>Price per unit</label>
        <div class="sd-tbte__input">
          <input
            type="text"
            class="form-control"
            :value="item.price.value"
            @change="onPriceChange(index, $event)">
          <span>USD</span>
        </div>
        <p class="sd-tbte__error" v-show="item.price.error">{{ item.price.error }}</p>
      </div>
      <div class="col-sm-3" v-if="index !== 0 && items.length-1 === index">
        <button class="sd-tbte__btn-remove" @click="removeItem(index)">Remove</button>
      </div>
    </div>
  </div>
`,
  methods: {
    clearErrors() {
      const
        me = this,
        items = me.items;
      let len = items.length;
      while (len--) {
        new Extend(items[len], {
          start: { error: null },
          end: { error: null },
          price: { error: null },
        }, true).merge();
      }
      me.error = null;
    },
    /**
     * Creates item object with default values.
     * @param {Null/undefined/Object} data
     * @return {Object}
     */
    createItem(data) {
      return new Extend({
        id: uuid(),
        start: {
          value: '',
          error: null,
        },
        end: {
          value: '999999999',
          disabled: false,
          error: null,
        },
        price: {
          value: 0,
          error: null,
        }
      }, data || {}, true).merge();
    },
    /**
     * Function called when ending quantity input value changes.
     * @param {Integer} itemIndex
     * @param {Event} e
     */
    onEndingQuantityChange(itemIndex, e) {
      const
        me = this,
        items = me.items;
      let
        value = e.target.value;

      items[itemIndex].end.value = value;

      me.validate().then(() => {
        items.push(me.createItem({
          start: {
            value: parseInt(value) + 1,
          },
        }));
        me.updateInput();
      });
    },
    onPriceChange(itemIndex, e) {
      this.items[itemIndex].price.value = parseFloat(e.target.value);
      this.validate().then(() => {
        this.updateInput();
      });
    },
    /**
     * Remove item by index.
     * @param {Integer} itemIndex
     */
    removeItem(itemIndex) {
      const items = this.items;
      items.splice(itemIndex, 1);
      items[items.length-1].end.value = 999999999;
      this.updateInput();
    },
    /**
     * Stringify items and insert it into input.
     */
    updateInput() {
      this.input.value = JSON.stringify(this.items);
    },
    validate() {
      const me = this;

      if (me.validationReq) {
        me.validationReq.abort();
      }

      me.loading = true;

      me.validationReq = $.ajax({
        url: me.validationApiEndpoint,
        type: 'post',
        data: {
          items: JSON.stringify(me.items),
        },
        success() {
          me.clearErrors();
        },
        error(xhr) {
          me.clearErrors();
          const data = xhr.responseJSON;
          if (data.unexpected) {
            me.error = data.message;
          } else {
            const key = data.property.split('.')[0];
            me.items[data.itemIndex][key].error = data.message;
          }
        },
        complete() {
          me.loading = false;
          me.validationReq = null;
        }
      });

      return me.validationReq;
    }
  },
  created() {
    const me = this;
    me.validate().then(() => this.updateInput());
  },
});


export default TieredBillingTypeEditor;
